// Whether a scene carries questions — the signal auto-advance is gated on.
//
// It cannot be read off the scene's mandatory settings. Question scenes used to
// be marked `isMandatory` with `waitForAction` / `waitForCorrectAction`, and
// every auto-advance gate keyed on that pair; once the mandatory setting was
// dropped from question scenes in the editor, those gates stopped firing and a
// course-wide "auto next" from the global config started advancing straight
// past unanswered questions. The exercise map is the fact, the mandatory type
// was only ever a proxy for it.

// The Visor's content and plugin registry. An entry counts as a question only when its box
// is still there and holds an evaluation plugin; an unanswered question's entry may be
// empty, so the entry's contents say nothing. Without a registry every entry counts.
export function visorQuestionContent(root = globalThis.IdealStudio) {
  const plugins = root && root.Visor && root.Visor.Plugins;
  const state = (root && root.State) || {};
  if (!plugins) return null;
  return {
    boxesById: state.boxesById || {},
    pluginToolbarsById: state.pluginToolbarsById || {},
    isQuestionPlugin: (pluginId) => {
      try {
        return !!plugins[pluginId] && plugins[pluginId].getConfig().category === "evaluation";
      } catch (_) {
        return false;
      }
    },
  };
}

/**
 * The scene's question ids.
 * @param {object} exercisesById `IdealStudio.State.exercises`
 * @param {string} sceneId
 * @param {object|null} content `{ boxesById, pluginToolbarsById, isQuestionPlugin }`; null counts every entry
 * @returns {string[]}
 */
export function sceneQuestionIds(exercisesById, sceneId, content = visorQuestionContent()) {
  if (!exercisesById || !sceneId) return [];
  const entry = exercisesById[sceneId];
  // Every page gets an entry (VisorApp seeds one for progress tracking), so the
  // entry's presence means nothing — the nested map is what holds the questions.
  const ids = Object.keys((entry && entry.exercises) || {});
  if (!content) return ids;
  // Entries left by boxes that are gone or never were questions do not count.
  return ids.filter((id) => {
    const tb = content.pluginToolbarsById[id];
    return !!content.boxesById[id] && !!tb && content.isQuestionPlugin(tb.pluginId);
  });
}

/**
 * @param {object} exercisesById `IdealStudio.State.exercises`
 * @param {string} sceneId
 * @param {object|null} [content] see sceneQuestionIds
 * @returns {boolean}
 */
export function sceneHasQuestions(exercisesById, sceneId, content) {
  return sceneQuestionIds(exercisesById, sceneId, content).length > 0;
}

/**
 * Whether a scene holds the learner on an answer. Auto-advance and the
 * scene-end card both refuse to run over such a scene, so they share one rule
 * — written out separately at three call sites, it drifted twice.
 *
 * @param {object}  exercisesById `IdealStudio.State.exercises`
 * @param {string}  sceneId
 * @param {object}  toolbar       the scene's viewToolbar
 */
export function sceneIsAnswerGated({ exercisesById, sceneId, toolbar, content } = {}) {
  if (sceneHasQuestions(exercisesById, sceneId, content)) return true;
  const tb = toolbar || {};
  // Scenes gated on something that registers no exercise entry (a OneClick
  // marker, say). Counts only while the scene is actually mandatory —
  // imported content can carry a stale mandatoryType on a non-mandatory scene.
  return (
    !!tb.isMandatory &&
    (tb.mandatoryType === "waitForAction" || tb.mandatoryType === "waitForCorrectAction")
  );
}

/**
 * Whether the learner has answered this scene's questions. Correct or not does
 * not matter — `attempted` is the flag GradeComponent refreshes through its
 * setExercises callback on every submit, and the same one the last-scene
 * completion rule reads.
 *
 * @param {object} exercisesById the LIVE exercise state (VisorApp's
 *   `this.state.exercises`, not the static IdealStudio.State copy)
 * @param {string} sceneId
 */
export function sceneQuestionsAnswered(exercisesById, sceneId) {
  const entry = exercisesById && sceneId ? exercisesById[sceneId] : null;
  return !!entry && entry.attempted === true;
}
